import { sgettext } from '../../i18n.js';
import ActivityTemplateNameColumn from './ActivityTemplateNameColumn.svelte';
import TemplateStatusBadge from './TemplateStatusBadge.svelte';
import DateTimeColumn from '../DataGrid/ColumnRenderers/DateTimeColumn.svelte';
import BranchColumn from './BranchColumn.svelte';
import TypeBadge from './TypeBadge.svelte';
import AllElementsStatus from './AllElementsStatus.svelte';
import InstanceStatusColumn from './InstanceStatusColumn.svelte';
import OperationBypassStatusColumn from './OperationBypassStatusColumn.svelte';
import ArchivedInstanceStatusColumn from './ArchivedInstanceStatusColumn.svelte';
import CountsColumn from './CountsColumn.svelte';
import ImprovementName from './ImprovementName.svelte';
import ElementNameColumn from './ElementNameColumn.svelte';
import MainWorkCentreColumn from './MainWorkCentreColumn.svelte';
import ApproversColumn from './ApproversColumn.svelte';
import DaysPendingColumn from './DaysPendingColumn.svelte';
import TimeConfirmationSequenceStatusColumn from './TimeConfirmationSequenceStatusColumn.svelte';
import TimeConfirmationStatusColumn from './TimeConfirmationStatusColumn.svelte';
import OperationConfirmation from './OperationConfirmation.svelte';

let columns;
let tables;

const homePageSize = 50;
const homePageMWCSize = 1000;

const init = () => {
  // Delay initialisation of columns and tables until later.
  // On module import, the gettext function needed by sgettext
  // will not be available causing an early runtime error
  if (!columns || !tables) {
    columns = {
      default: {
        label: sgettext('$$NAME$$'),
      },
      activity_template_name: {
        label: sgettext('$$NAME$$'),
        renderer: ActivityTemplateNameColumn,
      },
      version_number: {
        label: sgettext('$$ACTIVITY_VERSION$$'),
      },
      template_status: {
        label: sgettext('$$STATUS$$'),
        renderer: TemplateStatusBadge,
      },
      element_status: {
        label: sgettext('$$STATUS$$'),
        renderer: AllElementsStatus,
      },
      created_date: {
        label: sgettext('$$CREATED$$'),
        renderer: DateTimeColumn,
      },
      last_modified_date: {
        label: sgettext('$$MODIFIED$$'),
        renderer: DateTimeColumn,
      },
      last_modified_date: {
        label: sgettext('$$MODIFIED$$'),
        renderer: DateTimeColumn,
      },
      branch_full_name: {
        label: sgettext('$$BRANCH$$'),
        renderer: BranchColumn,
      },
      element_name: {
        label: sgettext('$$NAME$$'),
        renderer: ElementNameColumn,
      },
      element_type: {
        label: sgettext('$$TYPE$$'),
        renderer: TypeBadge,
      },
      start_time: {
        label: sgettext('$$START$$'),
        renderer: DateTimeColumn,
        props: {
          all: 'true',
        },
      },
      activity_name: {
        label: sgettext('$$ACTIVITY_1$$'),
      },
      instance_status: {
        label: sgettext('$$STATUS$$'),
        renderer: InstanceStatusColumn,
      },
      issues_count: {
        label: sgettext('$$ISSUE_COUNT$$'),
        renderer: CountsColumn,
      },
      operation_bypass: {
        label: sgettext('$$OPERATION_BYPASS$$'),
        renderer: OperationBypassStatusColumn,
      },
      scheduled_start_time: {
        label: sgettext('$$SCHEDULED_START$$'),
        renderer: DateTimeColumn,
      },
      scheduled_end_time: {
        label: sgettext('$$SCHEDULED_FINISH$$'),
        renderer: DateTimeColumn,
      },
      manually_changed_instance_status: {
        label: sgettext('$$STATUS$$'),
        renderer: ArchivedInstanceStatusColumn,
      },
      end_time: {
        label: sgettext('$$COMPLETED$$'),
        renderer: DateTimeColumn,
      },
      improvement_name: {
        label: sgettext('$$ISSUE$$'),
        renderer: ImprovementName,
      },
      started_time: {
        label: sgettext('$$STARTED$$'),
        renderer: DateTimeColumn,
        props: {
          all: 'true',
        },
      },
      sap_work_order_number: {
        label: sgettext('$$WORK_ORDER_2$$'),
      },
      main_work_centre: {
        label: sgettext('$$MWC$$'),
        renderer: MainWorkCentreColumn,
      },
      superseded_elements_count: {
        label: sgettext('$$SUPERSEDED_ELEMENTS_3$$'),
        renderer: CountsColumn,
        props: {
          superseded_elements: 'true',
        },
      },
      days_pending: {
        label: sgettext('$$DAYS_PENDING$$'),
        renderer: DaysPendingColumn,
      },
      approvers: {
        label: sgettext('$$APPROVER$$'),
        renderer: ApproversColumn,
      },
      operation_number: {
        label: sgettext('$$OPERATION$$'),
      },
      sap_status: {
        label: sgettext('$$SAP_STATUS$$'),
      },
      tc_sequence_status: {
        label: sgettext('$$SEQUENCE_STATUS$$'),
        renderer: TimeConfirmationSequenceStatusColumn,
      },
      time_confirmation_status: {
        label: sgettext('$$SAP_STATUS$$'),
        renderer: TimeConfirmationStatusColumn,
      },
      operation_confirmation: {
        label: sgettext('$$OPERATION_CONFIRMATION$$'),
        renderer: OperationConfirmation,
      },
      instance_start_time: {
        label: sgettext('$$INSTANCE_ACTUAL_START$$'),
        renderer: DateTimeColumn,
      },
      document_number: {
        label: sgettext('$$DOCUMENT_NUMBER$$'),
      },
    };

    tables = {
      default: {
        name: getColumn('default'),
      },
      activities: {
        main_work_centre: getColumn('main_work_centre', 6),
        name: getColumn('activity_template_name', 35),
        superseded_elements_count: getColumn('superseded_elements_count', 20),
        last_modified_date: getColumn('last_modified_date', 11),
        template_status: getColumn('template_status', 16),
        days_pending: getColumn('days_pending', 10),
        approvers: getColumn('approvers', 25),
        branch_full_name: getColumn('branch_full_name', 24),
      },
      elements: {
        name: getColumn('element_name', 47),
        element_type: getColumn('element_type', 11),
        last_modified_date: getColumn('last_modified_date', 12),
        status_text: getColumn('element_status', 11),
        branch_full_name: getColumn('branch_full_name', 25),
      },
      schedule: {
        start_time: getColumn('started_time', 11),
        sap_work_order_number: getColumn('sap_work_order_number', 10),
        name: getColumn('activity_name', 11),
        issues_count: getColumn('issues_count', 9),
        operation_bypass: getColumn('operation_bypass', 10),
        scheduled_start_time: getColumn('scheduled_start_time', 11),
        scheduled_end_time: getColumn('scheduled_end_time', 11),
        end_time: getColumn('end_time', 11),
        instance_status: getColumn('instance_status', 10),
        branch_full_name: getColumn('branch_full_name', 16),
      },
      time_confirmations: {
        activity_name: getColumn('activity_name', 11),
        main_work_centre: getColumn('main_work_centre', 6),
        instance_start_time: getColumn('instance_start_time', 11),
        sap_work_order_number: getColumn('sap_work_order_number', 10),
        operation_number: getColumn('operation_number', 6),
        tc_sequence_status: getColumn('tc_sequence_status', 10),
        time_confiramtion_status: getColumn('time_confirmation_status', 10),
        operation_confirmation: getColumn('operation_confirmation', 10),
      },
      improvements: {
        value: getColumn('improvement_name', 45),
        activity_name: getColumn('activity_name', 16),
        created_date: getColumn('created_date', 15),
        branch_full_name: getColumn('branch_full_name', 24),
      },
      d2_instructions: {
        main_work_centre: getColumn('main_work_centre', 6),
        document_number: getColumn('document_number', 8),
        version_number: getColumn('version_number', 8),
        name: getColumn('activity_template_name', 26),
        created_date: getColumn('created_date', 12),
        publication_status: getColumn('publication_status', 28),
        branch_full_name: getColumn('branch_full_name', 19),
      },
    };
  }
};

const getColumn = (key, width, override = {}) => {
  const column = columns[key] || columns.default;
  return {
    ...column,
    ...(width ? { width: `${width}%`, style: `width: ${width}%` } : {}),
    ...override,
  };
};

const getColumns = (key, override = {}) => {
  init();
  const table = tables[key] || tables.default;
  return { ...table, ...override };
};

export { getColumns, homePageSize, homePageMWCSize };
